// src/components/foam/FoamLostAndFound.js
//
// Screen 6 · Lost & Found public view (`/r/:sku` when registered)
// Doc: 2026-06 design-system handoff "Screen 6" + SPEC §2.2 (amended 2026-06-05)
//
// 2026-06-04: WAKA Design System pass — CARBON ground, Saira headline, logo
// PNG, full-radius FOUND BOARD pill.
//
// CONTACT MODEL (amended by Nigel 2026-06-05 — supersedes §2.2's fallback):
// "Via WAKA" is REMOVED entirely. The primary CTA branches on the owner's
// shared channels only:
//   phone shared (+ preferred)  → tel: link
//   email shared (+ preferred)  → mailto: direct link
//   neither shared              → NO contact button. Finders see the board +
//                                 owner note only. Reachability is pure opt-in.
//
// 2026-06-05 additions:
//   - preview mode gets a Close → back to Owner Detail (onClosePreview)
//   - "Is this your board?" sign-in affordance for logged-out owners
//     (onOwnerSignIn) — prevents owners being stranded on the finder view.

import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { FScreen, FPage, FHeader, FWordmark, FLabel, FHeadline, FBrandText } from './FoamPrimitives';
import FoamVientoChat from './FoamVientoChat';

// ── Finder report form (Nigel, 2026-06-05) ──────────────────────────────────
// Shown ONLY on the real finder view when the owner has no public contact
// channel. The finder leaves where they found the board + their contact;
// reportFoundBoard emails WAKA the full private record for a manual reunite.
// Module scope — inputs defined inside a component remount per keystroke.
const inputCls = `
  w-full font-sans text-[15px] px-[14px]
  text-foam-ink placeholder:text-foam-mute
  focus:outline-none focus:ring-2 focus:ring-foam-orange border-0
`;

// Shopify variant deep links per board size — source: Viento's own product
// links in waka-agent/functions/src/knowledge-base.ts (verified 2026-06-05).
const BOARD_VARIANT_URLS = {
  '66': 'https://waka.surf/products/waka-glide-board?variant=44130412986526',
  '77': 'https://waka.surf/products/waka-glide-board?variant=44130412953758',
  '88': 'https://waka.surf/products/waka-glide-board?variant=44130412920990',
  '99': 'https://waka.surf/products/waka-glide-board?variant=44130412888222',
  '111': 'https://waka.surf/products/waka-glide-board?variant=45074514247838',
};
const BOARD_PRODUCT_URL = 'https://waka.surf/products/waka-glide-board';

// Curious-scanner section (Nigel, 2026-06-05): not every scan is a finder —
// some people scan because they want to know what the board IS. Give them
// the product page (exact variant) and a Viento chat seeded with the board.
const CuriousSection = ({ productName, size }) => {
  const [chatOpen, setChatOpen] = useState(false);
  const label = `WAKA ${productName || 'board'}${size ? ` ${size}` : ''}`;
  const url = BOARD_VARIANT_URLS[size] || BOARD_PRODUCT_URL;
  return (
    <div className="flex flex-col gap-[10px] pt-[6px]" style={{ borderTop: '1px solid rgba(255,255,255,0.16)' }}>
      <div className="font-mono text-[12px] tracking-[0.12em] uppercase mt-[8px]" style={{ color: 'var(--foam-mute)' }}>
        Just curious about this board?
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="
          h-[48px] w-full flex items-center justify-center gap-[10px]
          font-sans font-bold text-[13px] uppercase no-underline
        "
        style={{
          borderRadius: 0,
          letterSpacing: '0.12em',
          border: '1.5px solid rgba(255,255,255,0.4)',
          color: 'var(--foam-paper)',
          background: 'transparent',
        }}
      >
        About the {productName || 'board'} {size || ''} →
      </a>
      {!chatOpen ? (
        <button
          type="button"
          onClick={() => setChatOpen(true)}
          className="
            h-[48px] w-full flex items-center justify-center gap-[10px]
            font-sans font-bold text-[13px] uppercase
          "
          style={{
            borderRadius: 0,
            letterSpacing: '0.12em',
            border: '1.5px solid rgba(255,255,255,0.4)',
            color: 'var(--foam-paper)',
            background: 'transparent',
          }}
        >
          Ask Viento about it
        </button>
      ) : (
        <div className="p-[14px]" style={{ background: 'var(--foam-paper)' }}>
          <FoamVientoChat
            seed={`I just scanned a ${label} — can you tell me more about this board?`}
            placeholder="Ask Viento anything…"
          />
        </div>
      )}
    </div>
  );
};

// Viento handoff with CONTEXT (Nigel, 2026-06-05): native Foam chat (no
// iframe/widget chrome — FoamVientoChat) seeded with the found-board scenario
// + serial, so Viento responds appropriately from message one (knowledge-base
// FOUND/LOST BOARD protocol + report_found_board tool complete the loop).
const FoundViento = ({ sku }) => {
  const [open, setOpen] = useState(false);
  const handoff = `I found a WAKA board — serial ${sku}. I'd like to report it so the owner can get it back.`;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="
          h-[60px] w-full flex items-center justify-center gap-[10px]
          bg-foam-orange text-foam-carbon
          font-sans font-bold text-[15px] uppercase
          active:scale-[0.99] transition-transform
        "
        style={{ borderRadius: 0, letterSpacing: '0.12em' }}
      >
        Found this board? Tell us →
      </button>
    );
  }
  // Carbon ground behind the chat: wrap in a paper panel so the Foam chat
  // bubbles keep their contrast.
  return (
    <div className="p-[14px]" style={{ background: 'var(--foam-paper)' }}>
      <FoamVientoChat seed={handoff} placeholder="Reply to Viento…" />
    </div>
  );
};

const FoundReportForm = ({ sku }) => {
  const [foundLocation, setFoundLocation] = useState('');
  const [finderContact, setFinderContact] = useState('');
  const [message, setMessage] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [errorMsg, setErrorMsg] = useState(null);

  if (state === 'done') {
    return (
      <div className="p-[16px] text-[14px] leading-[1.5] text-center font-medium" style={{ background: '#22262C', color: 'var(--foam-paper)' }}>
        ✓ Thank you. WAKA has the details and will work on reuniting this board
        with its owner.
      </div>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!foundLocation.trim() || !finderContact.trim()) return;
        setState('sending');
        setErrorMsg(null);
        try {
          await httpsCallable(functions, 'reportFoundBoard')({
            sku, foundLocation, finderContact, message,
          });
          setState('done');
        } catch (err) {
          console.error('Found report failed:', err);
          setErrorMsg(err.message || 'Something went wrong. Please try again.');
          setState('error');
        }
      }}
      className="flex flex-col gap-[8px]"
    >
      <div className="font-mono text-[12px] tracking-[0.1em] uppercase" style={{ color: 'var(--foam-orange)' }}>
        Found this board? Let WAKA know
      </div>
      <input
        type="text"
        value={foundLocation}
        onChange={(e) => setFoundLocation(e.target.value)}
        placeholder="Where you found it"
        maxLength={200}
        className={inputCls}
        style={{ borderRadius: 0, height: 48, background: '#22262C', color: 'var(--foam-paper)' }}
      />
      <input
        type="text"
        value={finderContact}
        onChange={(e) => setFinderContact(e.target.value)}
        placeholder="Your email or phone"
        maxLength={200}
        className={inputCls}
        style={{ borderRadius: 0, height: 48, background: '#22262C', color: 'var(--foam-paper)' }}
      />
      <input
        type="text"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Anything else? (optional)"
        maxLength={500}
        className={inputCls}
        style={{ borderRadius: 0, height: 48, background: '#22262C', color: 'var(--foam-paper)' }}
      />
      {errorMsg && (
        <div className="font-mono text-[12px] tracking-[0.08em] uppercase text-foam-orange-d">
          ● {errorMsg}
        </div>
      )}
      <button
        type="submit"
        disabled={state === 'sending' || !foundLocation.trim() || !finderContact.trim()}
        className="
          h-[52px] w-full bg-foam-orange text-foam-carbon
          font-sans font-bold text-[14px] uppercase
          disabled:opacity-50 disabled:cursor-not-allowed
          active:scale-[0.99] transition-transform
        "
        style={{ borderRadius: 0, letterSpacing: '0.12em' }}
      >
        {state === 'sending' ? 'Sending…' : 'Tell WAKA →'}
      </button>
      <div className="text-[12px] text-center leading-[1.45]" style={{ color: '#9A9DA2' }}>
        WAKA will reach out to the owner directly. Your details go only to WAKA.
      </div>
    </form>
  );
};

const FoamLostAndFound = ({ publicRegistration, preview = false, onClosePreview, onOwnerSignIn }) => {
  // Defensive: if there's nothing to show, render an empty state
  if (!publicRegistration) {
    return (
      <FPage>
        <FHeader />
        <p className="text-[16px] text-foam-ink-2 mt-[40px] text-center">
          No board found with this serial. Double-check the link.
        </p>
      </FPage>
    );
  }

  const {
    registeredUserName,       // already filtered to first name only if showFirstName
    registeredUserEmail,      // null unless showEmail
    phoneNumber,              // null unless showPhone
    phoneIsWhatsApp,          // same number reachable on WhatsApp (wa.me)
    ownerCity,                // null unless showCity (default OFF per SPEC §2.3)
    ownerMessage,             // null unless showOwnerMessage
    preferredContact,         // 'phone' | 'email' | null
    productName,
    size,
    sku,
  } = publicRegistration;

  const firstName = registeredUserName || 'the owner';
  const phoneAvailable = !!phoneNumber;
  const emailAvailable = !!registeredUserEmail;

  // Friendly board name for owner-facing prefills — owners know their own
  // board; no serial needed (Nigel, 2026-06-05). e.g. "WAKA Glide 88".
  const boardLabel = `WAKA ${productName || 'board'}${size ? ` ${size}` : ''}`;

  // ALL shared channels render as buttons (Nigel, 2026-06-05) — the preferred
  // one leads as the big orange CTA, the rest follow as ghost buttons.
  // WhatsApp rides on the phone number via wa.me deep link.
  const ctas = [];
  if (phoneAvailable) {
    ctas.push({
      id: 'phone',
      label: `Call ${firstName} →`,
      href: `tel:${(phoneNumber || '').replace(/\s/g, '')}`,
      sub: phoneNumber,
    });
    if (phoneIsWhatsApp) {
      ctas.push({
        id: 'whatsapp',
        label: `WhatsApp ${firstName} →`,
        href: `https://wa.me/${(phoneNumber || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi! I found your ${boardLabel}.`)}`,
        sub: null,
      });
    }
  }
  if (emailAvailable) {
    ctas.push({
      id: 'email',
      label: `Email ${firstName} →`,
      href: `mailto:${registeredUserEmail}?subject=${encodeURIComponent(`Found your ${boardLabel}`)}`,
      sub: registeredUserEmail,
    });
  }
  // Preferred channel leads
  ctas.sort((a, b) => (a.id === preferredContact ? -1 : b.id === preferredContact ? 1 : 0));
  const primaryCta = ctas[0] || null;
  const secondaryCtas = ctas.slice(1);

  const header = (
    <div
      className="flex justify-between items-center pb-[14px]"
      style={{ borderBottom: '1.5px solid rgba(255,255,255,0.22)' }}
    >
      <FWordmark dark />
      <div className="flex items-center gap-[10px]">
        <div
          className="font-mono text-[12px] uppercase font-bold px-[9px] py-[5px]"
          style={{
            background: 'var(--foam-orange)',
            color: 'var(--foam-carbon)',
            letterSpacing: '0.1em',
            borderRadius: 40, // status pills are the full-radius exception
          }}
        >
          {preview ? 'Preview' : 'Found Board'}
        </div>
        {preview && onClosePreview && (
          <button
            type="button"
            onClick={onClosePreview}
            aria-label="Close preview"
            className="font-mono text-[12px] uppercase font-bold px-[9px] py-[5px]"
            style={{
              background: 'transparent',
              color: 'var(--foam-paper)',
              border: '1.5px solid rgba(255,255,255,0.4)',
              letterSpacing: '0.1em',
              borderRadius: 40,
            }}
          >
            ✕ Close
          </button>
        )}
      </div>
    </div>
  );

  const footer = (
    <>
      {primaryCta ? (
        <>
          <a
            href={primaryCta.href}
            className="
              h-[60px] w-full flex items-center justify-center gap-[10px]
              bg-foam-orange text-foam-carbon
              font-sans font-bold text-[15px] uppercase no-underline
              active:scale-[0.99] transition-transform
            "
            style={{ borderRadius: 0, letterSpacing: '0.12em' }}
          >
            {primaryCta.label}
          </a>
          {primaryCta.sub && (
            <div className="font-mono text-[12px] tracking-[0.08em] text-center" style={{ color: 'var(--foam-mute)' }}>
              {primaryCta.sub}
            </div>
          )}
          {secondaryCtas.map((cta) => (
            <a
              key={cta.id}
              href={cta.href}
              target={cta.id === 'whatsapp' ? '_blank' : undefined}
              rel={cta.id === 'whatsapp' ? 'noreferrer' : undefined}
              className="
                h-[52px] w-full flex items-center justify-center gap-[10px]
                font-sans font-bold text-[13px] uppercase no-underline
                active:scale-[0.99] transition-transform
              "
              style={{
                borderRadius: 0,
                letterSpacing: '0.12em',
                border: '1.5px solid rgba(255,255,255,0.4)',
                color: 'var(--foam-paper)',
                background: 'transparent',
              }}
            >
              {cta.label}
            </a>
          ))}
        </>
      ) : preview ? (
        <div
          className="p-[16px] text-[14px] leading-[1.5] text-center font-medium"
          style={{ background: '#22262C', color: '#D7D4C8' }}
        >
          No contact channel shared — finders will see a "Let WAKA know" form
          instead, and WAKA will contact you using your registration details.
        </div>
      ) : (
        // Real finder + owner unreachable → Viento handles the report
        // conversationally (location, finder contact → emails WAKA).
        <FoundViento sku={sku} />
      )}

      {/* "Drop a pin" removed 2026-06-05 (Nigel) — location is collected
          through the Viento found-board flow instead. */}

      <div className="text-[12px] text-center leading-[1.45] font-medium mt-[4px]" style={{ color: '#9A9DA2' }}>
        {primaryCta
          ? `${firstName}'s contact is shown only because they opted in.`
          : 'Contact details are never shown unless the owner opts in.'}
      </div>

      {!preview && onOwnerSignIn && (
        <button
          type="button"
          onClick={onOwnerSignIn}
          className="font-mono text-[12px] tracking-[0.08em] uppercase underline text-center w-full py-[6px]"
          style={{ background: 'transparent', color: 'var(--foam-paper)' }}
        >
          Is this your board? Sign in
        </button>
      )}
    </>
  );

  return (
    <FScreen dark header={header} footer={footer}>
      <div className="mt-[4px]">
        <FLabel color="var(--foam-orange)">
          {preview ? 'This is what a finder sees' : 'You scanned a board belonging to'}
        </FLabel>

        <div className="mt-[14px]">
          <FHeadline size={64} lineHeight={0.88} color="var(--foam-paper)">
            {registeredUserName ? `${registeredUserName}'s` : 'Lost'}
            <FHeadline.Accent block>WAKA.</FHeadline.Accent>
          </FHeadline>
        </div>

        <p className="text-[17px] leading-[1.4] font-medium mt-[18px]" style={{ color: 'var(--foam-paper)' }}>
          A{' '}
          <FBrandText size={14} color="var(--foam-paper)" style={{ letterSpacing: '0.04em' }}>
            {(productName || 'BOARD').toUpperCase()} {size || ''}
          </FBrandText>
          {ownerCity ? <>, last seen in <strong>{ownerCity}</strong></> : ''}
          .{' '}
          {registeredUserName ? `${registeredUserName.split(' ')[0]} would love it back.` : "Let's get it home."}
        </p>
      </div>

      {ownerMessage && (
        <div className="p-[18px]" style={{ background: '#22262C', color: 'var(--foam-paper)' }}>
          <div
            className="font-mono text-[12px] uppercase font-medium mb-[10px]"
            style={{ color: 'var(--foam-orange)', letterSpacing: '0.12em' }}
          >
            A note from the owner
          </div>
          <p className="text-[17px] leading-[1.4] font-medium" style={{ color: 'var(--foam-paper)' }}>
            "{ownerMessage}"
          </p>
        </div>
      )}

      {/* Curious scanners — product page (exact variant) + Viento (Nigel, 2026-06-05).
          The reunion CTAs stay in the pinned footer; this lives lower in the
          scroll so the screen remains reunion-first. */}
      <CuriousSection productName={productName} size={size} />
    </FScreen>
  );
};

export default FoamLostAndFound;
